import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff, User, Building2, Mail, Phone, MapPin, CreditCard, CheckCircle, FileText } from 'lucide-react';

export default function Signup() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    businessAddress: ''
  });
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    businessName: ''
  });
  const navigate = useNavigate();

  const validateStep1 = () => {
    const errors = {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      businessName: ''
    };

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.businessName.trim()) {
      errors.businessName = 'Business name is required';
    }

    setValidationErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleNextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
      setError(null);
    }
  };

  const handleBackStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setError(null);
    }
  };

  const handleCreateAccount = async () => {
    try {
      setLoading(true);

      // Validate terms agreement
      if (!agreeToTerms) {
        setError('You must agree to the Terms of Service to create an account');
        setLoading(false);
        return;
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // 1. Create user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('No user returned from signup');

      // 2. Create business
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .insert([{
          owner_id: authData.user.id,
          name: formData.businessName,
          email: formData.businessEmail || formData.email,
          phone: formData.businessPhone,
          address: formData.businessAddress,
          subscription_status: 'inactive' // Will be activated when they complete payment
        }])
        .select()
        .single();

      if (businessError) throw businessError;

      // 3. Update user metadata with business ID
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          owned_business_ids: [businessData.id]
        }
      });

      if (metadataError) {
        console.warn('Failed to update user metadata, but business was created:', metadataError);
      }

      // 4. Update user profile with names
      const { error: userError } = await supabase
        .from('users')
        .update({
          first_name: formData.firstName,
          last_name: formData.lastName
        })
        .eq('id', authData.user.id);

      if (userError) throw userError;

      // 5. Set setup status to "pending" - user is not fully logged in yet
      const { error: setupError } = await supabase
        .from('user_setup_status')
        .insert([{
          user_id: authData.user.id,
          setup_step: 'pending',
          current_step: 1,
          completed: false
        }]);

      if (setupError) {
        console.warn('Failed to create setup status, but account was created:', setupError);
      }

      // 6. Redirect to subscription activation immediately after account creation
      navigate('/admin-subscription-management', { 
        state: { 
          userId: authData.user.id,
          email: formData.email,
          fromSignup: true,
          message: 'Account created successfully! Please select and activate your subscription to continue.' 
        }
      });

    } catch (err) {
      console.error('Signup error:', err);
      let errorMessage = 'Failed to create account';
      
      if (err instanceof Error) {
        if (err.message.includes('already registered')) {
          errorMessage = 'An account with this email already exists';
        } else if (err.message.includes('password')) {
          errorMessage = 'Password must be at least 6 characters';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 flex items-center justify-center relative overflow-hidden px-4" style={{
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }}>
      {/* Subtle Tech Pattern Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>

      {/* Main Container */}
      <div className="w-full max-w-2xl relative z-10 transform transition-all duration-700 ease-out animate-in slide-in-from-bottom-4 fade-in-0">
        {/* Logo Section */}
        <div className="text-center mb-8 relative">
          {/* Brain Wave Pulse Animation Background */}
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Concentric Pulse Rings */}
            <div className="absolute">
              <div className="pulse-ring pulse-ring-1"></div>
              <div className="pulse-ring pulse-ring-2"></div>
              <div className="pulse-ring pulse-ring-3"></div>
            </div>
            
            {/* EEG Brain Wave Lines */}
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
              <div className="brain-wave brain-wave-1"></div>
              <div className="brain-wave brain-wave-2"></div>
              <div className="brain-wave brain-wave-3"></div>
            </div>
          </div>
          
          {/* Logo (positioned above animation) */}
          <div className="relative z-10">
            <h1 className="text-5xl font-black text-white mb-4 tracking-wider" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
              Nexus <span className="text-4xl align-super">AI</span>
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-white/60 to-transparent mx-auto"></div>
            <p className="mt-4 text-white/80 text-lg font-medium">Create Your Account</p>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-purple-400' : 'text-white/40'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                currentStep >= 1 ? 'border-purple-400 bg-purple-400/20' : 'border-white/40'
              }`}>
                <span className="text-sm font-semibold">1</span>
              </div>
              <span className="ml-2 text-sm font-medium">Account Info</span>
            </div>
            <div className={`w-8 h-0.5 ${currentStep >= 2 ? 'bg-purple-400' : 'bg-white/20'}`}></div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-purple-400' : 'text-white/40'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                currentStep >= 2 ? 'border-purple-400 bg-purple-400/20' : 'border-white/40'
              }`}>
                <span className="text-sm font-semibold">2</span>
              </div>
              <span className="ml-2 text-sm font-medium">Setup</span>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl shadow-purple-900/20 transition-all duration-300 ease-out hover:shadow-purple-900/30" style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)'
        }}>
          {currentStep === 1 ? (
            /* Step 1: Personal Information */
            <div className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-500/20 border border-red-400/30 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-red-300 text-sm text-center">{error}</p>
                </div>
              )}

              {/* Personal Information Section */}
              <div className="space-y-4">
                <h3 className="text-white/90 text-lg font-semibold flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      className={`w-full px-4 py-4 bg-black/20 border ${
                        validationErrors.firstName ? 'border-red-400' : 'border-purple-400/30'
                      } rounded-xl text-white placeholder-gray-300 transition-all duration-300 ease-out focus:outline-none focus:border-purple-500 focus:bg-black/30 focus:shadow-lg focus:shadow-purple-500/20 hover:bg-black/25 touch-manipulation`}
                      style={{ fontSize: '16px' }}
                    />
                    {validationErrors.firstName && (
                      <p className="text-red-400 text-sm ml-1">{validationErrors.firstName}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      className={`w-full px-4 py-4 bg-black/20 border ${
                        validationErrors.lastName ? 'border-red-400' : 'border-purple-400/30'
                      } rounded-xl text-white placeholder-gray-300 transition-all duration-300 ease-out focus:outline-none focus:border-purple-500 focus:bg-black/30 focus:shadow-lg focus:shadow-purple-500/20 hover:bg-black/25 touch-manipulation`}
                      style={{ fontSize: '16px' }}
                    />
                    {validationErrors.lastName && (
                      <p className="text-red-400 text-sm ml-1">{validationErrors.lastName}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-purple-400" />
                    <input
                      type="text"
                      placeholder="Business Name"
                      required
                      value={formData.businessName}
                      onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
                      className={`w-full px-4 py-4 pl-12 bg-black/20 border ${
                        validationErrors.businessName ? 'border-red-400' : 'border-purple-400/30'
                      } rounded-xl text-white placeholder-gray-300 transition-all duration-300 ease-out focus:outline-none focus:border-purple-500 focus:bg-black/30 focus:shadow-lg focus:shadow-purple-500/20 hover:bg-black/25 touch-manipulation`}
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  {validationErrors.businessName && (
                    <p className="text-red-400 text-sm ml-1">{validationErrors.businessName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-purple-400" />
                    <input
                      type="email"
                      placeholder="Email Address"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className={`w-full px-4 py-4 pl-12 bg-black/20 border ${
                        validationErrors.email ? 'border-red-400' : 'border-purple-400/30'
                      } rounded-xl text-white placeholder-gray-300 transition-all duration-300 ease-out focus:outline-none focus:border-purple-500 focus:bg-black/30 focus:shadow-lg focus:shadow-purple-500/20 hover:bg-black/25 touch-manipulation`}
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  {validationErrors.email && (
                    <p className="text-red-400 text-sm ml-1">{validationErrors.email}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className={`w-full px-4 py-4 pr-12 bg-black/20 border ${
                        validationErrors.password ? 'border-red-400' : 'border-purple-400/30'
                      } rounded-xl text-white placeholder-gray-300 transition-all duration-300 ease-out focus:outline-none focus:border-purple-500 focus:bg-black/30 focus:shadow-lg focus:shadow-purple-500/20 hover:bg-black/25 touch-manipulation`}
                      style={{ fontSize: '16px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-purple-400 hover:text-purple-300 transition-all duration-200 ease-out hover:scale-110 active:scale-95 p-1 rounded-lg touch-manipulation"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <p className="text-red-400 text-sm ml-1">{validationErrors.password}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm Password"
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className={`w-full px-4 py-4 pr-12 bg-black/20 border ${
                        validationErrors.confirmPassword ? 'border-red-400' : 'border-purple-400/30'
                      } rounded-xl text-white placeholder-gray-300 transition-all duration-300 ease-out focus:outline-none focus:border-purple-500 focus:bg-black/30 focus:shadow-lg focus:shadow-purple-500/20 hover:bg-black/25 touch-manipulation`}
                      style={{ fontSize: '16px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-purple-400 hover:text-purple-300 transition-all duration-200 ease-out hover:scale-110 active:scale-95 p-1 rounded-lg touch-manipulation"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {validationErrors.confirmPassword && (
                    <p className="text-red-400 text-sm ml-1">{validationErrors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Next Button */}
              <button
                type="button"
                onClick={handleNextStep}
                className="w-full bg-purple-700 hover:bg-purple-600 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg shadow-purple-700/30 hover:shadow-purple-600/40 touch-manipulation"
                style={{ minHeight: '44px' }}
              >
                <span className="uppercase tracking-wider">Next Step</span>
              </button>

              {/* Sign In Link */}
              <div className="text-center pt-4 border-t border-white/10">
                <p className="text-white/60 text-sm mb-3">Already have an account?</p>
                <Link
                  to="/login"
                  className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-all duration-200 ease-out hover:underline underline-offset-4 decoration-purple-400/50 hover:decoration-purple-300 transform hover:scale-105 inline-block p-2 rounded-lg touch-manipulation"
                >
                  Sign In
                </Link>
              </div>
            </div>
          ) : (
            /* Step 3: Account Setup Section */
            <div className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-500/20 border border-red-400/30 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-red-300 text-sm text-center">{error}</p>
                </div>
              )}

              {/* Account Setup Section */}
              <div className="space-y-4">
                <h3 className="text-white/90 text-lg font-semibold flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Account Setup
                </h3>
                
                <div className="bg-purple-500/10 border border-purple-400/30 rounded-xl p-6">
                  <div className="text-center">
                    <CheckCircle className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                    <h4 className="text-white/90 text-lg font-semibold mb-2">Ready to Create Account</h4>
                    <p className="text-white/70 text-sm mb-4">
                      Your account will be created and you'll be able to select your subscription plan.
                    </p>
                    <div className="flex items-center justify-center space-x-2 text-purple-400">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium">Almost Ready</span>
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* Terms of Service Section */}
                <div className="bg-purple-500/10 border border-purple-400/30 rounded-xl p-6">
                  <div className="space-y-4">
                    <h4 className="text-white/90 text-lg font-semibold flex items-center">
                      <FileText className="h-5 w-5 mr-2" />
                      Terms of Service
                    </h4>
                    
                    <div className="bg-black/20 rounded-lg p-4 max-h-40 overflow-y-auto">
                      <div className="text-white/70 text-sm space-y-3">
                        <p><strong>1. Acceptance of Terms</strong></p>
                        <p>By creating an account, accessing, or using the Service, you agree to comply with and be bound by these Terms, our Privacy Policy, and all applicable laws and regulations. We may update these Terms from time to time, and continued use of the Service after such changes constitutes your acceptance of the updated Terms.</p>
                        
                        <p><strong>2. Service Description</strong></p>
                        <p>Nexus AI provides cloud-based business management software, including, but not limited to, booking systems, customer relationship management (CRM) tools, team collaboration features, and related functionalities. The Service is provided on a subscription basis and is accessible through a license granted to you upon payment of applicable fees.</p>
                        
                        <p><strong>3. License Grant and Restrictions</strong></p>
                        <p>Upon subscribing to the Service and paying the applicable fees, Nexus AI grants you a non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Service solely for your internal business purposes. You may not copy, modify, distribute, sell, or lease any part of the Service or its underlying software.</p>
                        
                        <p><strong>4. User Responsibilities</strong></p>
                        <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to use the Service only for lawful purposes and in compliance with these Terms.</p>
                        
                        <p><strong>5. Payment Terms</strong></p>
                        <p>The Service is provided on a subscription basis, with fees billed monthly in advance. All fees are non-refundable, except as required by law. You may cancel your subscription at any time through your account settings.</p>
                        
                        <p><strong>6. Termination</strong></p>
                        <p>We reserve the right to suspend or terminate your account and access to the Service at our sole discretion, with or without notice, for any reason, including breach of these Terms, non-payment of fees, or suspected fraudulent activity.</p>
                        
                        <p><strong>7. Limitation of Liability</strong></p>
                        <p>To the maximum extent permitted by law, Nexus AI will not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service.</p>
                        
                        <p><strong>8. Privacy Policy</strong></p>
                        <p>Your use of the Service is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information. By using the Service, you consent to the practices described in the Privacy Policy.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="agreeToTerms"
                        checked={agreeToTerms}
                        onChange={(e) => setAgreeToTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 text-purple-600 bg-black/20 border-purple-400/30 rounded focus:ring-purple-500 focus:ring-2 focus:ring-offset-0 focus:ring-offset-transparent"
                      />
                      <label htmlFor="agreeToTerms" className="text-white/80 text-sm leading-relaxed">
                        I have read and agree to the <span className="text-purple-400 font-medium">Terms of Service</span> and <span className="text-purple-400 font-medium">Privacy Policy</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={handleBackStep}
                  className="flex-1 bg-transparent border border-purple-400/30 hover:border-purple-400 text-purple-400 hover:text-purple-300 font-semibold py-4 px-6 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
                  style={{ minHeight: '44px' }}
                >
                  <span className="uppercase tracking-wider">Back</span>
                </button>
                
                <button
                  type="button"
                  onClick={handleCreateAccount}
                  disabled={loading || !agreeToTerms}
                  className="flex-1 bg-purple-700 hover:bg-purple-600 disabled:bg-purple-700/50 disabled:bg-gray-600 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 disabled:scale-100 disabled:cursor-not-allowed shadow-lg shadow-purple-700/30 hover:shadow-purple-600/40 touch-manipulation"
                  style={{ minHeight: '44px' }}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Creating Account...</span>
                    </div>
                  ) : (
                    <span className="uppercase tracking-wider">
                      {agreeToTerms ? 'Create Account' : 'Agree to Terms to Continue'}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        /* Brain Wave Pulse Animation Styles */
        .pulse-ring {
          position: absolute;
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          animation: heartbeat 3s ease-in-out infinite;
        }
        
        .pulse-ring-1 {
          width: 120px;
          height: 120px;
          margin: -60px 0 0 -60px;
          animation-delay: 0s;
        }
        
        .pulse-ring-2 {
          width: 160px;
          height: 160px;
          margin: -80px 0 0 -80px;
          animation-delay: 0.5s;
        }
        
        .pulse-ring-3 {
          width: 200px;
          height: 200px;
          margin: -100px 0 0 -100px;
          animation-delay: 1s;
        }
        
        @keyframes heartbeat {
          0%, 20%, 40%, 60%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.1;
            border-color: rgba(255, 255, 255, 0.1);
          }
          10% {
            transform: scale(1.1);
            opacity: 0.3;
            border-color: rgba(255, 255, 255, 0.3);
          }
          30% {
            transform: scale(1.2);
            opacity: 0.4;
            border-color: rgba(255, 255, 255, 0.4);
          }
        }
        
        /* EEG Brain Wave Lines */
        .brain-wave {
          position: absolute;
          width: 300px;
          height: 2px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transform-origin: center;
        }
        
        .brain-wave-1 {
          top: 20%;
          left: -150px;
          animation: brainWave1 4s ease-in-out infinite;
          transform: rotate(-15deg);
        }
        
        .brain-wave-2 {
          top: 50%;
          left: -150px;
          animation: brainWave2 3.5s ease-in-out infinite;
          animation-delay: 1s;
        }
        
        .brain-wave-3 {
          top: 80%;
          left: -150px;
          animation: brainWave3 4.5s ease-in-out infinite;
          animation-delay: 2s;
          transform: rotate(15deg);
        }
        
        @keyframes brainWave1 {
          0%, 100% {
            transform: translateX(0) rotate(-15deg) scaleX(1);
            opacity: 0;
          }
          25% {
            transform: translateX(100px) rotate(-15deg) scaleX(1.5);
            opacity: 0.3;
          }
          50% {
            transform: translateX(200px) rotate(-15deg) scaleX(2);
            opacity: 0.6;
          }
          75% {
            transform: translateX(300px) rotate(-15deg) scaleX(1.5);
            opacity: 0.3;
          }
        }
        
        @keyframes brainWave2 {
          0%, 100% {
            transform: translateX(0) scaleX(1);
            opacity: 0;
          }
          20% {
            transform: translateX(80px) scaleX(1.2);
            opacity: 0.4;
          }
          50% {
            transform: translateX(200px) scaleX(2.5);
            opacity: 0.8;
          }
          80% {
            transform: translateX(320px) scaleX(1.2);
            opacity: 0.4;
          }
        }
        
        @keyframes brainWave3 {
          0%, 100% {
            transform: translateX(0) rotate(15deg) scaleX(1);
            opacity: 0;
          }
          30% {
            transform: translateX(120px) rotate(15deg) scaleX(1.3);
            opacity: 0.2;
          }
          60% {
            transform: translateX(240px) rotate(15deg) scaleX(1.8);
            opacity: 0.5;
          }
          90% {
            transform: translateX(360px) rotate(15deg) scaleX(1.3);
            opacity: 0.2;
          }
        }
      `}</style>
    </div>
  );
}